import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@primitives/button";
import { Textarea } from "@primitives/textarea";
import { toast } from "@/hooks/useToast";
import { useAuthState } from "@/zustand/AuthState";
import { reportError } from "./reportError";
import type { ErrorBoundaryFallbackProps } from "./ErrorBoundary";

type Status = "idle" | "sending" | "sent" | "failed";

export function ErrorFallback({
  error,
  componentStack,
  eventId,
  scope,
  reset,
}: ErrorBoundaryFallbackProps) {
  const { t } = useTranslation();
  const username = useAuthState((state) => state.username);

  const [showReport, setShowReport] = React.useState(false);
  const [userMessage, setUserMessage] = React.useState("");
  const [status, setStatus] = React.useState<Status>("idle");
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) {
      return;
    }
    const id = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(id);
  }, [copied]);

  const isFullScreen = scope === "root" || scope === "route";

  async function handleSubmit() {
    if (status === "sending") {
      return;
    }
    setStatus("sending");
    try {
      await reportError({
        error,
        componentStack,
        eventId,
        scope,
        userMessage,
        username,
      });
      setStatus("sent");
      toast({
        description: t("errorBoundary.reportSent"),
      });
    } catch {
      setStatus("failed");
      toast({
        variant: "destructive",
        description: t("errorBoundary.reportFailed"),
      });
    }
  }

  async function handleCopyId() {
    try {
      await navigator.clipboard.writeText(eventId);
      setCopied(true);
    } catch {
      // ignore — copy is best-effort
    }
  }

  const containerClass = isFullScreen
    ? "flex min-h-screen w-full items-center justify-center p-6"
    : "flex h-full w-full items-center justify-center p-6";

  return (
    <div className={containerClass} role="alert" aria-live="assertive">
      <div className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-input bg-background p-6 shadow-sm">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold">{t("errorBoundary.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("errorBoundary.description")}
          </p>
        </div>

        <div className="flex flex-col gap-2 rounded-md bg-muted p-3 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">
              {t("errorBoundary.eventIdLabel")}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopyId}
              aria-label={t("errorBoundary.copyEventId")}
            >
              {copied
                ? t("errorBoundary.copiedEventId")
                : t("errorBoundary.copyEventId")}
            </Button>
          </div>
          <code className="font-mono break-all">{eventId || "—"}</code>
        </div>

        {showReport && status !== "sent" && (
          <Textarea
            rows={4}
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            placeholder={t("errorBoundary.reportPlaceholder")}
            aria-label={t("errorBoundary.reportPlaceholder")}
            className="resize-none"
            disabled={status === "sending"}
          />
        )}

        <div className="flex flex-col gap-2">
          {scope !== "root" && (
            <Button
              type="button"
              onClick={reset}
              variant="default"
              disabled={status === "sending"}
            >
              {t("errorBoundary.tryAgain")}
            </Button>
          )}
          {isFullScreen && (
            <Button
              type="button"
              variant={scope === "root" ? "default" : "outline"}
              onClick={() => window.location.reload()}
              disabled={status === "sending"}
            >
              {t("errorBoundary.reload")}
            </Button>
          )}
          {status !== "sent" && (
            <Button
              type="button"
              variant="outline"
              onClick={showReport ? handleSubmit : () => setShowReport(true)}
              disabled={status === "sending"}
            >
              {!showReport
                ? t("errorBoundary.report")
                : status === "sending"
                  ? t("errorBoundary.sending")
                  : t("errorBoundary.send")}
            </Button>
          )}
        </div>

        {import.meta.env.DEV && (
          <details className="text-xs text-muted-foreground">
            <summary className="cursor-pointer">
              {t("errorBoundary.details")}
            </summary>
            <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-2">
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ""}
              {componentStack ? `\n\n${componentStack}` : ""}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
